import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Redirect,
  Link,
  useHistory,
  useParams,
} from 'react-router-dom'
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons'
import {
  Spin,
  Row,
  Col,
  Button,
  Input,
  Breadcrumb,
  Form,
  message,
  Upload,
  Modal,
  Select,
  Switch,
  Tag,
} from 'antd'
import '../../css/surgeon.scss'
import util from '../../util/util'
import { addEditCustomer } from '../../store/action/customer.action'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { fetchCustomer } from '../../store/action/customer.action'
import ApolloClientService from '../../services/apolloclient.service'
import {
  normalizeClinicCode,
  isValidClinicCode,
  isClinicCodeError,
  CLINIC_CODE_RANGE_MESSAGE,
} from '../../util/cliniccode'

const apolloService = new ApolloClientService()
var form = undefined

const CustomerAdd = (props) => {
  const [image, setImage] = useState(undefined)
  const [customer, setCustomer] = useState(undefined)
  const [accesscode, setCode] = useState(undefined)
  const [surgeryTypes, setSurgeryTypes] = useState([])
  const [selectedSurgeryTypes, setSelectedSurgeryTypes] = useState([])
  const [allowPatientToContactClinic, setAllowPatientToContactClinic] = useState(true)
  const [clinicContactMessage, setClinicContactMessage] = useState('')
  const [allowPatientToSendWoundImages, setAllowPatientToSendWoundImages] = useState(true)

  var history = useHistory()
  var params = useParams()
  var customerid = params.id
  useEffect(() => {
    getCustomer()
    fetchSurgeryTypes()
  }, [])
  useEffect(() => {
    initializeForm()
  }, [customer, surgeryTypes])
  // A new clinic starts with a blank code on purpose: leaving it blank is how
  // an admin asks the server to assign one.

  var fetchSurgeryTypes = async () => {
    try {
      const types = await apolloService.getActiveSurgeryTypes()
      if (types && types.length > 0) {
        setSurgeryTypes(types)

        // Set default to Knee if adding new customer and no selection
        if (!customerid && selectedSurgeryTypes.length === 0) {
          const kneeDefault = types.find(st => st.name.toLowerCase() === 'knee')
          if (kneeDefault) {
            setSelectedSurgeryTypes([kneeDefault._id])
          }
        }
      } else {
        setSurgeryTypes([])
      }
    } catch (error) {
      message.error('Failed to load surgery types')

      setSurgeryTypes([])
    }
  }

  var getCustomer = async () => {
    if (!customerid) return
    var response = await props.getCustomer(customerid)
    if (!response) return
    setCustomer(response)
  }

  const handleCancel = () => {
    history.push('/customers')
  }

  var initializeForm = () => {
    if (!customer) return
    if (!form) return

    setImage(customer?.logo)
    // Kept to compare against on submit: changing a clinic's code locks out
    // patients still signing in with the old one.
    setCode(normalizeClinicCode(customer?.accesscode))
    setAllowPatientToContactClinic(customer?.allowPatientToContactClinic !== false)
    setClinicContactMessage(customer?.clinicContactMessage || '')
    setAllowPatientToSendWoundImages(customer?.allowPatientToSendWoundImages !== false)

    // Set selected surgery types
    if (customer?.surgerytypes && customer.surgerytypes.length > 0) {
      const surgeryTypeIds = customer.surgerytypes.map(st => st._id)
      setSelectedSurgeryTypes(surgeryTypeIds)
    } else if (surgeryTypes.length > 0 && selectedSurgeryTypes.length === 0) {
      const kneeDefault = surgeryTypes.find(st => st.name.toLowerCase() === 'knee')
      if (kneeDefault) {
        setSelectedSurgeryTypes([kneeDefault._id])
      }
    }

    form.setFieldsValue({
      ...customer,
      contact_name: customer?.contact?.name,
      contact_email: customer?.contact?.email,
      contact_phone: customer?.contact?.phone,
      ...customer?.address,
    })
  }

  var handleChange = (info) => {
    if (info.file.status === 'done')
      util.getBase64(info.file.originFileObj, (image) => setImage(image))
  }

  // Mirrors the server rules. Uniqueness is not checked here - only the server
  // can do that - so a duplicate comes back as an error message on save.
  var validateClinicCode = (rule, value) => {
    var code = normalizeClinicCode(value)

    if (!code) {
      // Blank means "assign one", which only makes sense for a clinic that has
      // no code yet: either brand new, or an old one that never got assigned.
      return !customerid || !accesscode
        ? Promise.resolve()
        : Promise.reject(new Error('Please enter a clinic code'))
    }

    // An existing code that predates these rules is left alone until someone
    // actually changes it, so unrelated edits are not blocked by it.
    if (code === accesscode) return Promise.resolve()

    return isValidClinicCode(code)
      ? Promise.resolve()
      : Promise.reject(new Error(CLINIC_CODE_RANGE_MESSAGE))
  }

  var handleSubmit = (value) => {
    // Validate that at least one surgery type is selected
    if (!selectedSurgeryTypes || selectedSurgeryTypes.length === 0) {
      message.error('Please select at least one surgery type')
      return
    }

    var input = {},
      contact = {},
      address = {}
    contact.phone = value.contact_phone
    contact.email = value.contact_email
    contact.name = value.contact_name
    address.line1 = value.line1
    address.line2 = value.line2
    address.city = value.city
    address.state = value.state
    address.zip = value.zip
    input._id = customerid
    input.name = value.name
    input.address = address
    input.contact = contact
    input.logo = image || ''
    input.company = props.company._id
    // Blank is meaningful: it asks the server to assign a code.
    input.accesscode = normalizeClinicCode(value.accesscode)
    input.surgerytypes = selectedSurgeryTypes
    input.allowPatientToContactClinic = allowPatientToContactClinic
    input.clinicContactMessage = clinicContactMessage || null
    input.allowPatientToSendWoundImages = allowPatientToSendWoundImages

    var save = () =>
      props.addEditCustomer(input).then((response) => {
        if (response && response.error) {
          // Uniqueness and range are settled by the server, so its refusal is
          // shown against the field the admin has to fix.
          if (isClinicCodeError(response.message) && form) {
            form.setFields([
              { name: 'accesscode', errors: [response.message] },
            ])
          } else {
            message.error(response.message)
          }
          return
        }
        if (response) {
          setCustomer(undefined)
          setImage(undefined)
          history.push('/customers')
        }
      })

    // Replacing a code that patients are already using is worth a second look.
    if (accesscode && input.accesscode !== accesscode) {
      Modal.confirm({
        title: 'Change this clinic code?',
        icon: <ExclamationCircleOutlined />,
        content:
          'Patients sign in with the clinic code. Changing it from ' +
          accesscode +
          ' to ' +
          input.accesscode +
          ' means anyone still using ' +
          accesscode +
          ' will not be able to sign in.',
        okText: 'Change it',
        cancelText: 'Cancel',
        onOk: save,
      })
      return
    }

    save()
  }

  const uploadButton = (
    <div className='uploadText'>
      <p className='ant-upload-drag-icon'>
        <InboxOutlined />
      </p>
      <p className='ant-upload-text'>
        Click or drag file to this area to upload
      </p>
    </div>
  )

  return props.token == undefined ? (
    <Redirect to='/login' />
  ) : (
    <Col className='surgeonAddContainer'>
      <Row>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to='/customers'>Clinics</Link>
          </Breadcrumb.Item>
          {customerid ? (
            <Breadcrumb.Item>Edit</Breadcrumb.Item>
          ) : (
            <Breadcrumb.Item>Add</Breadcrumb.Item>
          )}
        </Breadcrumb>
      </Row>
      <Row align='middle' className='headerRow'>
        <Col className='headerText'>CLINIC</Col>
      </Row>
      <Form ref={(e) => (form = e)} layout={'vertical'} onFinish={handleSubmit}>
        <Row gutter={20}>
          <Col flex='33%'>
            <Form.Item
              label='Name'
              name='name'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Please enter name',
                },
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item
              label='Clinic Code'
              name='accesscode'
              tooltip={
                'Patients enter this code to sign in. Leave blank to have one assigned automatically. ' +
                CLINIC_CODE_RANGE_MESSAGE.replace('Clinic code must be', 'Must be') +
                '.'
              }
              extra={
                customerid
                  ? undefined
                  : 'Leave blank and a code will be assigned automatically.'
              }
              rules={[{ validator: validateClinicCode }]}
            >
              <Input maxLength={4} placeholder={customerid ? '' : 'Auto-assigned if blank'} />
            </Form.Item>
            <Form.Item
              label='Surgery Types'
              required
              rules={[
                {
                  required: true,
                  message: 'Please select at least one surgery type',
                },
              ]}
            >
              <Select
                mode='multiple'
                placeholder='Select surgery types'
                value={selectedSurgeryTypes}
                onChange={(value) => setSelectedSurgeryTypes(value)}
                style={{ width: '100%' }}
              >
                {surgeryTypes.map((surgeryType) => (
                  <Select.Option key={surgeryType._id} value={surgeryType._id}>
                    {surgeryType.description}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label='Allow patients to contact clinic via Patient App'>
              <Switch
                checked={allowPatientToContactClinic}
                onChange={(checked) => setAllowPatientToContactClinic(checked)}
                checkedChildren='On'
                unCheckedChildren='Off'
              />
            </Form.Item>
            <Form.Item
              label='Allow patients to send wound images'
              extra={
                allowPatientToSendWoundImages
                  ? undefined
                  : 'The SEND WOUND IMAGE button is hidden in the patient app, and the WOUND_IMAGE reminder is switched off and locked.'
              }
            >
              <Switch
                checked={allowPatientToSendWoundImages}
                onChange={(checked) => setAllowPatientToSendWoundImages(checked)}
                checkedChildren='On'
                unCheckedChildren='Off'
              />
            </Form.Item>
            {!allowPatientToContactClinic && (
              <Form.Item
                label='Message shown to patients when messaging is disabled'
                extra='Leave blank to use the default message'
              >
                <Input.TextArea
                  rows={4}
                  value={clinicContactMessage}
                  onChange={(e) => setClinicContactMessage(e.target.value)}
                  placeholder='Your surgery team prefers that patients contact them directly for any medical or care questions. Please call them directly or use your patient portal to get in touch.'
                  maxLength={500}
                />
              </Form.Item>
            )}
            <Form.Item
              label='Contact - Name'
              name='contact_name'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Please enter contact name',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label='Contact - Email'
              name='contact_email'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  type: 'email',
                  message: 'Please enter contact email',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label='Contact - Phone'
              name='contact_phone'
              rules={[
                { required: true, message: 'Please enter contact phone' },
              ]}
            >
              <PhoneInput
                defaultCountry={'US'}
                className='phone-input'
                enableAreaCodes
              />
            </Form.Item>
          </Col>
          <Col flex='33%'>
            <Form.Item style={{ marginBottom: 6 }} label='Address' name='line1'>
              <Input.TextArea
                style={{ height: 56 }}
                placeholder='Line 1'
                rows={3}
              />
            </Form.Item>
            {/* <Form.Item name="line2" >
                            <Input.TextArea placeholder="Line 2" rows={1}/>
                        </Form.Item> */}
            <Form.Item label='City' name='city'>
              <Input />
            </Form.Item>
            <Form.Item label='State' name='state'>
              <Input />
            </Form.Item>
            <Form.Item label='Zip' name='zip'>
              <Input maxLength={9} />
            </Form.Item>
          </Col>
          <Col flex='33%'>
            <Row justify='center'>
              <Form.Item
                name='logo'
                rules={[{ required: false, message: 'Please upload a logo' }]}
                label='Logo'
              >
                <Upload.Dragger
                  listType='picture-card'
                  showUploadList={false}
                  onChange={handleChange}
                  customRequest={({ file, onSuccess }) =>
                    setTimeout(() => onSuccess('ok'), 0)
                  }
                >
                  {image ? (
                    <img
                      className='uploadPrev'
                      src={image}
                      alt='avatar'
                      style={{ width: '100%' }}
                    />
                  ) : (
                    uploadButton
                  )}
                </Upload.Dragger>
              </Form.Item>
            </Row>
            <Row justify='center'>
              {' '}
              {image ? (
                <DeleteOutlined style={{}} onClick={() => setImage('')} />
              ) : (
                '820 x 1800px (recommended)'
              )}
            </Row>
          </Col>
        </Row>
        <Row justify='end'>
          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              className='submit-form-button'
            >
              {customerid ? 'Update' : 'Add'}
            </Button>
            <Button
              style={{ marginLeft: 20 }}
              type='primary'
              className='submit-form-button'
              onClick={handleCancel}
            >
              Cancel
              {/* <Link to={"/customers"}>Cancel</Link> */}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </Col>
  )
}

CustomerAdd.propTypes = {
  isLoading: PropTypes.bool,
  token: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    token: util.verifyToken(state.profile.token),
    isLoading: state.profile.isLoading,
    customer: state.profile.customer,
    company: state.profile.company,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getCustomer: (customerid) => dispatch(fetchCustomer(customerid)),
    addEditCustomer: (params) => dispatch(addEditCustomer(params)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomerAdd)
